import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { deactivateTrailerType, listTrailerTypes, reactivateTrailerType, type TrailerType } from '../../../reference-data.api';

export type TrailerTypesState = { status: 'loading' } | { status: 'loaded'; types: TrailerType[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TrailerTypesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** Same shape as useTruckClasses: the catalogue and its row actions; create and edit are on the type's own page. */
export function useTrailerTypes() {
  const [state, setState] = useState<TrailerTypesState>({ status: 'loading' });
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listTrailerTypes()
      .then((types) => {
        if (!cancelled) setState({ status: 'loaded', types });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', types: await listTrailerTypes() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const deactivate = (row: TrailerType) => rowAction(() => deactivateTrailerType(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: TrailerType) => rowAction(() => reactivateTrailerType(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, deactivate, reactivate };
}
